import ProfileCompletionModal from "@components/ProfileCompletionModal";
import {
  CloudUploadIcon,
  ConfirmModal,
  FileNativeInput,
  handleApiRequest,
  useFileHandler,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import {
  ResumeAssessmentsReadyEnum,
  UploadResumeFileMutation,
  UploadResumeFileMutationVariables,
  UploadType,
  useUploadResumeFileMutation,
} from "@graphql/generated";
import { Button } from "@heroui/react";
import { useProfile } from "@hooks/useProfile";
import React, { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";

interface IUploadResumeFileProps {
  onUploadSuccess?: () => void;
  buttonClassName?: string;
  iconColor?: string;
  containerClassName?: string;
}
export const UploadResumeFile: React.FC<IUploadResumeFileProps> = ({
  onUploadSuccess,
  buttonClassName,
  iconColor = "fill-primary-500",
  containerClassName,
}) => {
  const [isNameIncomplete, setIsNameIncomplete] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const inputElementRef = useRef<HTMLInputElement>(null);
  const [uploadResumeFile, { loading: uploading }] = useUploadResumeFileMutation();
  const { fetchProfile, profile } = useProfile();

  const openFilePicker = useCallback(() => {
    if (!profile.firstName || !profile.lastName) {
      setIsNameIncomplete(true);
      return;
    }

    // If user has existing assessments (not NO_RESUME), show confirmation modal first
    if (profile.resumeAssessmentsReady && profile.resumeAssessmentsReady !== ResumeAssessmentsReadyEnum.NO_RESUME) {
      setIsConfirmModalOpen(true);
    } else {
      // No existing assessments, open file picker directly
      inputElementRef.current?.click();
    }
  }, [profile.firstName, profile.lastName, profile.resumeAssessmentsReady]);

  const checkValidation = (file?: File) => {
    if (file?.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return false;
    }
    if (file?.size > 1024 * 1024 * 5) {
      toast.error("File size must be less than 5MB");
      return false;
    }
    return true;
  };

  const { setFile, handleUpload, isLoading } = useFileHandler({
    uploadType: UploadType.RESUME,
    initialValue: null,
    userType: UserTypeEnum.USER,
  });

  const handleUploadFile = useCallback(
    async (resumeFile: File) => {
      if (!resumeFile) {
        toast.error("No file selected for upload");
        return;
      }

      try {
        setFile(resumeFile);

        const fileId = await handleUpload(resumeFile);

        if (!fileId) {
          toast.error("Failed to upload resume file");
          return;
        }

        await handleApiRequest<UploadResumeFileMutation, UploadResumeFileMutationVariables>({
          apiFunction: uploadResumeFile,
          variables: {
            file: String(fileId),
          },
          componentName: "ResumeUploader",
          actionName: "uploadResumeFile",
          successMessage: "Resume uploaded successfully!",
          fallbackErrorMessage: "Failed to save resume. Please try again.",
          extraErrorContext: {
            fileId,
            fileName: resumeFile.name,
            fileSize: resumeFile.size,
          },
          onSuccess: () => {
            fetchProfile();
            onUploadSuccess?.();
          },
        });
      } catch (error) {
        console.error("Resume upload process failed:", error);
        toast.error("Something went wrong during resume upload. Please try again.");
      }
    },
    [setFile, handleUpload, uploadResumeFile, fetchProfile, onUploadSuccess],
  );

  const handleSelectFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const isFileValid = checkValidation(file);

      if (isFileValid && file) {
        handleUploadFile(file);
      }

      // Reset the input value to allow selecting the same file again
      event.target.value = "";
    },
    [handleUploadFile],
  );

  const handleComplete = useCallback(() => {
    fetchProfile();
    setIsNameIncomplete(false);
    inputElementRef.current?.click();
  }, [fetchProfile]);

  const handleConfirm = useCallback(() => {
    setIsConfirmModalOpen(false);
    // Open file picker after user confirms
    inputElementRef.current?.click();
  }, []);

  const handleCancel = () => {
    setIsConfirmModalOpen(false);
  };

  return (
    <div className={`mt-4 ${containerClassName}`}>
      <FileNativeInput ref={inputElementRef} onChange={handleSelectFile} accept="application/pdf" />
      <Button
        onPress={openFilePicker}
        variant="bordered"
        className={`text-primary-500 border-primary-500 w-full text-xs ${buttonClassName}`}
        isLoading={isLoading || uploading}
        startContent={<CloudUploadIcon width={20} viewBox="0 0 44 44" className={iconColor ?? "fill-primary-500"} />}
      >
        Upload Resume
      </Button>
      <ProfileCompletionModal isUserUploaded={isNameIncomplete} onComplete={handleComplete} />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={handleCancel}
        cancelLabel="Cancel"
        size="2xl"
        title="Upload New Resume"
        confirmLabel="Yes, Upload New Resume"
        onConfirm={handleConfirm}
        messageClassName="!text-md text-gray-700"
        message="Uploading a new resume will delete your previous assessments and generate new ones based on your updated resume. Are you sure you want to continue?"
        confirmButtonClassName="bg-primary-500 hover:bg-primary-600 text-white"
      />
    </div>
  );
};
